import { SseParser } from './sseParser';

/**
 * One connection to a run's event stream (chat-runs-api.md §4), read with
 * fetch() because EventSource cannot send the Authorization header. The run
 * store decides what to do when it ends (reconnect, reconcile, give up).
 */

export interface RunEvent {
  /** The event's `seq` (the SSE `id`). */
  seq: number;
  type: 'stage' | 'delta' | 'citations' | 'error' | 'complete' | string;
  data: Record<string, any>;
}

export type StreamEnd =
  /** The server closed the stream (after `complete`, or after 10 minutes). */
  | { kind: 'ended' }
  /** We closed it. */
  | { kind: 'aborted' }
  /** The server answered with an error status. */
  | { kind: 'http'; status: number }
  /** The connection failed or dropped. */
  | { kind: 'network' };

export interface StreamDeps {
  fetch: typeof fetch;
  authorization: () => Promise<string | undefined>;
  url: (runId: string) => string;
  /**
   * A connection that sends nothing for this long is dropped (and the run
   * store reconnects): the server sends a heartbeat every 15 s (§4), so
   * silence means a dead connection (sleep, network change, proxy).
   */
  idleTimeoutMs?: number;
}

/** Three missed heartbeats. */
export const DEFAULT_IDLE_TIMEOUT_MS = 45_000;

export interface ReadStreamOptions {
  runId: string;
  /** The last `seq` processed; the server resumes after it. */
  lastSeq: number;
  signal: AbortSignal;
  onEvent: (event: RunEvent) => void;
  /** The server's reconnection hint (`retry: 3000`). */
  onRetryHint?: (ms: number) => void;
  /** The stream answered 200 and is being read. */
  onOpen?: () => void;
}

export const readRunStream = async (
  deps: StreamDeps,
  { runId, lastSeq, signal, onEvent, onRetryHint, onOpen }: ReadStreamOptions,
): Promise<StreamEnd> => {
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  const authorization = await deps.authorization();
  if (authorization) headers.Authorization = authorization;
  if (lastSeq > 0) headers['Last-Event-ID'] = String(lastSeq);

  // This connection only: the caller's signal ends the whole run's stream.
  const connection = new AbortController();
  const closeConnection = () => connection.abort();
  signal.addEventListener('abort', closeConnection);
  let idle: ReturnType<typeof setTimeout> | undefined;
  let stalled = false;
  const resetIdle = () => {
    clearTimeout(idle);
    idle = setTimeout(() => {
      stalled = true;
      connection.abort();
    }, deps.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS);
  };
  const lost = (): StreamEnd =>
    signal.aborted && !stalled ? { kind: 'aborted' } : { kind: 'network' };

  resetIdle();
  let response: Response;
  try {
    response = await deps.fetch(deps.url(runId), {
      headers,
      signal: connection.signal,
      cache: 'no-store',
    });
  } catch {
    clearTimeout(idle);
    signal.removeEventListener('abort', closeConnection);
    return lost();
  }
  if (!response.ok || !response.body) {
    clearTimeout(idle);
    signal.removeEventListener('abort', closeConnection);
    return { kind: 'http', status: response.status };
  }
  onOpen?.();

  const parser = new SseParser({
    onRetry: ms => onRetryHint?.(ms),
    onMessage: message => {
      if (signal.aborted) return;
      const seq = Number(message.id);
      if (!Number.isInteger(seq) || seq <= 0) return;
      let data: Record<string, any>;
      try {
        data = JSON.parse(message.data);
      } catch {
        return;
      }
      if (!data || typeof data !== 'object') return;
      onEvent({ seq, type: message.event, data });
    },
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const abort = () => {
    void reader.cancel().catch(() => undefined);
  };
  connection.signal.addEventListener('abort', abort);
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // Any bytes, heartbeat comments included, show the connection lives.
      resetIdle();
      parser.feed(decoder.decode(value, { stream: true }));
      if (connection.signal.aborted) return lost();
    }
    if (connection.signal.aborted) return lost();
    parser.feed(decoder.decode());
    parser.end();
    return { kind: 'ended' };
  } catch {
    return lost();
  } finally {
    clearTimeout(idle);
    connection.signal.removeEventListener('abort', abort);
    signal.removeEventListener('abort', closeConnection);
  }
};
